/* TrainingFlow — English-only candidate simulator (Batch 1).
 *
 * Phases:
 *   instructions   → English welcome + visual icon roadmap.
 *   board          → 5 task cards (different scenarios) — candidate picks one.
 *   work           → 2-tab work surface: Training Portal | Visa Application Demo.
 *   result         → Final assessment report.
 *
 * Critical rules:
 *   • Candidate UI is ENGLISH ONLY. No Arabic strings anywhere on the
 *     candidate path. Admin preview banner is the only exception.
 *   • No customer phone numbers, no agency name/logo, no real names.
 *     Names are masked as Applicant #001, Domestic Worker #001, Sponsor #001.
 *   • Skip-stage button is admin-preview only (?preview=1).
 *   • Anti-cheat: paste, copy, refresh, back-nav, tab visibility all blocked
 *     and counted as warnings. 3 warnings → forced failure.
 *   • 30-minute global cap.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import {
  FileText, ExternalLink, FormInput, Calendar, FileDown, Upload,
  ShieldCheck, AlertTriangle, Clock, MapPin, Plane, Users, Briefcase,
  Globe, ChevronRight, ChevronLeft, Languages,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CITY = { riyadh: "Riyadh", jeddah: "Jeddah", dammam: "Dammam" };
const TIMER_SECS = 30 * 60;
const MAX_WARNINGS = 3;

const SCENARIO_LABELS = {
  tr_maid:       "Turkey — Domestic Worker with Saudi Sponsor",
  fr_family:     "Schengen — Family + Domestic Worker",
  fr_individual: "Schengen — Individual Saudi",
  ca_bil:        "Canada — Biometrics / BIL Flow",
  ksa_resident:  "Schengen — Non-Saudi Resident",
};
const SPECIAL_BADGES = {
  tr_maid:       { text: "Domestic Worker", color: "#a855f7" },
  fr_family:     { text: "Family + Maid",   color: "#0ea5e9" },
  fr_individual: { text: "Solo Saudi",      color: "#10b981" },
  ca_bil:        { text: "BIL Required",    color: "#f59e0b" },
  ksa_resident:  { text: "Resident",        color: "#ec4899" },
};

function countdownInfo(travelDate) {
  if (!travelDate) return { label: "New", color: "#3b82f6" };
  const days = Math.ceil((new Date(travelDate) - new Date()) / 86400000);
  if (days < 0)   return { label: "Overdue",            color: "#dc2626", days };
  if (days <= 3)  return { label: `Urgent · ${days}d`,  color: "#ef4444", days };
  if (days <= 7)  return { label: `${days} days left`,  color: "#f59e0b", days };
  if (days <= 10) return { label: `${days} days left`,  color: "#fbbf24", days };
  return            { label: "New",                     color: "#3b82f6", days };
}

export default function TrainingFlow() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const isPreview = search.get("preview") === "1";
  const [phase, setPhase] = useState("instructions");
  const [tasks, setTasks] = useState([]);
  const [isDemoShare, setIsDemoShare] = useState(false);
  const [pickedTask, setPickedTask] = useState(null);
  const [err, setErr] = useState("");
  const [warnings, setWarnings] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(TIMER_SECS);
  const [activeTab, setActiveTab] = useState("portal");
  const [missing, setMissing] = useState([]);
  const [notes, setNotes] = useState([]);
  const [demo, setDemo] = useState({});
  const [apptSlots, setApptSlots] = useState([]);
  const [pickedSlots, setPickedSlots] = useState([]);
  const [noSlots, setNoSlots] = useState(false);
  const [result, setResult] = useState(null);
  // Phase 2: wizard, PDF, upload, admin AR toggle
  const [wizardStep, setWizardStep] = useState(0); // Step 0 = Sign In
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const [proofFileName, setProofFileName] = useState("");
  const [adminArabic, setAdminArabic] = useState(false);
  const [signedIn, setSignedIn] = useState(false); // gates Steps 1..5

  // Neutralize the global app-shell brand identifiers for ANY visitor of this
  // route (candidate or admin). VTA/visatravelsa/Arabic title come from
  // public/index.html and would otherwise leak into the candidate experience.
  // We restore on unmount so the rest of the app is unaffected.
  useEffect(() => {
    const prev = { title: document.title, desc: "", appName: "", appleTitle: "" };
    const descEl = document.querySelector('meta[name="description"]');
    const appNameEl = document.querySelector('meta[name="application-name"]');
    const appleTitleEl = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (descEl) prev.desc = descEl.getAttribute("content") || "";
    if (appNameEl) prev.appName = appNameEl.getAttribute("content") || "";
    if (appleTitleEl) prev.appleTitle = appleTitleEl.getAttribute("content") || "";
    document.title = "Training Demo — Assessment Sandbox";
    if (descEl) descEl.setAttribute("content", "Candidate training sandbox");
    if (appNameEl) appNameEl.setAttribute("content", "Training Demo");
    if (appleTitleEl) appleTitleEl.setAttribute("content", "Training Demo");
    return () => {
      document.title = prev.title;
      if (descEl) descEl.setAttribute("content", prev.desc);
      if (appNameEl) appNameEl.setAttribute("content", prev.appName);
      if (appleTitleEl) appleTitleEl.setAttribute("content", prev.appleTitle);
    };
  }, []);

  // Load 5 task cards once
  useEffect(() => {
    if (phase !== "board" || tasks.length) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/ats/training/tasks/${token}`);
        setTasks(data.tasks || []);
        setIsDemoShare(!!data.is_demo_share);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load tasks");
      }
    })();
  }, [phase, token, tasks.length]);

  // Open one task — initialize appointment slots
  const openTask = (t) => {
    const hasSlots = (Math.abs((t.task_number || "0").charCodeAt(t.task_number.length - 1)) % 10) >= 3;
    setApptSlots(hasSlots ? generateSlots(t) : []);
    setNoSlots(!hasSlots);
    setPickedTask(t);
    setWizardStep(0);
    setSignedIn(false);
    setPdfGenerated(false);
    setPdfUploaded(false);
    setProofFileName("");
    setDemo({});
    setPickedSlots([]);
    setPhase("work");
  };

  // Anti-cheat
  const bumpWarning = () => setWarnings(w => {
    const next = w + 1;
    if (next >= MAX_WARNINGS) finalize({ forcedFail: true });
    return next;
  });
  useEffect(() => {
    if (phase !== "work" || isPreview) return;
    const bump = () => bumpWarning();
    const onVis = () => { if (document.visibilityState === "hidden") bump(); };
    // Copy/paste are NOT blocked inside the demo — candidates may use
    // translation tools and reference content from their notes. Tab
    // switching, refresh and back-nav remain tracked to ensure focus.
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    const onPop = () => { window.history.pushState(null, ""); bump(); };
    window.history.pushState(null, "");
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("popstate", onPop);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("popstate", onPop);
    };
  }, [phase, isPreview]);

  // Timer
  useEffect(() => {
    if (phase !== "work") return;
    const t = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) { clearInterval(t); finalize({ timeout: true }); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [phase]);

  const finalize = async (extra = {}) => {
    // Gate: PDF must be generated AND uploaded before submission
    // (except in forcedFail / timeout cases which bypass the gate).
    if (!extra.forcedFail && !extra.timeout) {
      if (!signedIn)     { alert("Please sign in to the Visa Application Demo (Step 0) first."); return; }
      if (!pdfGenerated) { alert("Please generate the Demo Appointment Confirmation PDF first (Step 5)."); return; }
      if (!pdfUploaded)  { alert("Please attach the PDF in the Training Portal → Appointment tab before submitting."); return; }
    }
    const r = scoreDemo({ task: pickedTask, demo, pickedSlots, noSlots, missing, notes, warnings });
    const payload = {
      demo_session_ids: [],
      missing_requests: missing,
      operational_notes: notes,
      appointment_outcome: noSlots ? { kind: "no_slots" }
        : (pickedSlots[0] ? { kind: "selected", datetime: pickedSlots[0].date + "T" + pickedSlots[0].time, city: pickedSlots[0].city } : null),
      appointment_fee: pickedSlots[0]?.fee || null,
      proof_uploaded: pdfUploaded,
    };
    if (!isPreview) {
      try { await axios.post(`${API}/ats/training/result/${token}`, payload); } catch (_) { /* swallow */ }
      // Per-task report — used by ATS Dashboard "Task report" drawer.
      try {
        const m0 = pickedTask?.members?.[0] || {};
        const bioCityExpected = pickedTask?.biometric_city || "";
        const bioCityEntered = pickedSlots?.[0]?.city || "";
        const norm = (v) => String(v ?? "").trim().toLowerCase();
        const auditRow = (label_ar, expected, entered, error_ar) => {
          const e = norm(entered), x = norm(expected);
          let status = "ok";
          if (!e) status = "missing";
          else if (e !== x) status = "wrong";
          return {
            label_ar,
            expected: expected || "—",
            entered: entered || "—",
            status,
            error_ar: status === "ok" ? "" : (status === "missing" ? "لم يُدخَل" : error_ar),
          };
        };
        const fieldAudit = [
          auditRow("الدولة المستهدفة", pickedTask?.country, demo.country, "اختلاف الدولة"),
          auditRow("نوع التأشيرة", pickedTask?.visa_type, demo.visa_type, "اختلاف نوع التأشيرة"),
          auditRow("الاسم الكامل", m0.full_name || m0.name, demo.full_name, "اختلاف اسم مقدّم الطلب"),
          auditRow("رقم الجواز", m0.passport_number, demo.passport_number, "خطأ في رقم الجواز"),
        ];
        if (m0.national_id || /saudi|سعود/i.test(pickedTask?.country || "")) {
          fieldAudit.push(auditRow("رقم الهوية الوطنية", m0.national_id, demo.national_id, "خطأ في الهوية الوطنية"));
        }
        if (m0.iqama_number) {
          fieldAudit.push(auditRow("رقم الإقامة", m0.iqama_number, demo.iqama_number, "خطأ في رقم الإقامة"));
        }
        if (m0.sponsor_name) {
          fieldAudit.push(auditRow("اسم الكفيل", m0.sponsor_name, demo.sponsor_name, "اختلاف اسم الكفيل"));
        }
        if (m0.date_of_birth || m0.dob) {
          fieldAudit.push(auditRow("تاريخ الميلاد", m0.date_of_birth || m0.dob, demo.date_of_birth || demo.dob, "خطأ في تاريخ الميلاد"));
        }
        if (bioCityExpected) {
          fieldAudit.push(auditRow("مدينة البصمة", bioCityExpected, bioCityEntered, "اختيار مدينة بصمة غير صحيحة"));
        }
        if (pickedTask?.scenario_id === "ca_bil") {
          const expBil = pickedTask?.bil_issued ? "yes" : "no";
          fieldAudit.push(auditRow("معالجة BIL (كندا)", expBil, demo.bil_issued, "خطأ في منطق BIL"));
        }
        const fe = {
          country_mismatch:           !!(r.checks || []).find(c => /country/i.test(c[0]) && c[0].startsWith("FAIL")),
          visa_type_mismatch:         !!(r.checks || []).find(c => /visa type/i.test(c[0]) && c[0].startsWith("FAIL")),
          passport_mismatch:          !!(r.checks || []).find(c => /passport/i.test(c[0]) && c[0].startsWith("FAIL")),
          national_id_mismatch:       !!(demo.national_id && pickedTask?.members?.[0]?.national_id && demo.national_id !== pickedTask.members[0].national_id),
          iqama_mismatch:             !!(demo.iqama_number && pickedTask?.members?.[0]?.iqama_number && demo.iqama_number !== pickedTask.members[0].iqama_number),
          biometric_city_mismatch:    !!(r.checks || []).find(c => /city|biometric/i.test(c[0]) && c[0].startsWith("FAIL")),
          applicant_sponsor_mismatch: !!(r.checks || []).find(c => /applicant|sponsor/i.test(c[0]) && c[0].startsWith("FAIL")),
          bil_handling_error:         pickedTask?.scenario_id === "ca_bil" && demo.bil_issued === "yes" && !pickedTask?.bil_issued,
        };
        const taskReport = {
          task_id: pickedTask?.task_number || "unknown",
          scenario: pickedTask?.visa_type || "unknown",
          opened_at: new Date(Date.now() - (TIMER_SECS - secondsLeft) * 1000).toISOString(),
          completed_at: new Date().toISOString(),
          time_spent_seconds: TIMER_SECS - secondsLeft,
          field_errors: fe,
          field_audit: fieldAudit,
          missing_document_handling: missing.length ? `${missing.length} request(s) flagged` : "none",
          appointment_handling: noSlots ? "no_slots" : (pickedSlots[0] ? "selected" : "skipped"),
          pdf_generated: pdfGenerated,
          pdf_uploaded: pdfUploaded,
          final_task_result: (r.score >= 65 && !extra.forcedFail && !extra.timeout) ? "completed" : "incomplete",
        };
        await axios.post(`${API}/ats/training/task-report/${token}`, taskReport);
      } catch (_) { /* swallow — report submit is best-effort */ }
    }
    setResult({ ...r, warnings, secondsUsed: TIMER_SECS - secondsLeft, ...extra, pdfGenerated, pdfUploaded });
    setPhase("result");
  };

  // PDF generator (Demo Appointment Confirmation)
  // Builds the PDF and returns the filename; callers update state.
  const generateConfirmationPdf = () => {
    if (!pickedTask) return;
    const fname = buildConfirmationPdf(pickedTask, pickedSlots, demo);
    if (fname) {
      setPdfGenerated(true);
      setProofFileName(fname);
    }
  };

  // ── INSTRUCTIONS ──
  if (phase === "instructions") {
    return (
      <Shell isPreview={isPreview}>
        <h1 style={s.h1}>Visa Operations — Practical Demo</h1>
        <h2 style={{ ...s.h1, fontSize: 18, color: "#cbd5e1", marginTop: -8, direction: "rtl" }}>التجربة العملية — إدخال بيانات التأشيرات</h2>
        <div style={s.welcomeBox}>
          You are entering a training simulation. <b style={s.gold}>No real customer data is used.</b><br />
          We evaluate how accurately you read a task, fill the application, and complete the workflow.
        </div>
        <div style={{ ...s.welcomeBox, direction: "rtl", marginTop: 8, fontSize: 13 }}>
          هذه تجربة تدريبية فقط. <b style={s.gold}>لا تُستخدم بيانات عملاء حقيقيين.</b> هدفنا قياس دقّتك في قراءة المهمّة وتعبئة الطلب وإكمال الخطوات.
        </div>

        <h2 style={s.h2}>How it works · كيف تجري التجربة</h2>
        <div style={s.iconRoadmap}>
          <RoadmapStep Icon={FileText}     title="Read task data" />
          <RoadmapStep Icon={ExternalLink} title="Open visa demo" />
          <RoadmapStep Icon={FormInput}    title="Fill application" />
          <RoadmapStep Icon={Calendar}     title="Select appointments" />
          <RoadmapStep Icon={FileDown}     title="Generate confirmation" />
          <RoadmapStep Icon={Upload}       title="Return and upload result" />
        </div>

        <div style={{ padding: 14, marginTop: 12, background: "rgba(13,124,121,0.08)", border: "1px solid rgba(13,124,121,0.4)", borderRadius: 10 }}>
          <div style={{ fontSize: 13.5, color: "#a7f3d0", lineHeight: 1.8 }}>
            <b>The demo environment is in English</b>, similar to real visa application portals. You may use translation or suitable tools when needed. The goal is to complete the case accurately.
          </div>
          <div style={{ fontSize: 13, color: "#cbd5e1", direction: "rtl", marginTop: 8, lineHeight: 2 }}>
            بيئة التجربة باللغة الإنجليزية تماماً كما هي بوّابات التأشيرات الحقيقية. يمكنك استخدام أدوات الترجمة أو أي أدوات مساعدة لفهم البيانات وتعبئتها. الهدف هو إكمال المعاملة بشكل صحيح، وليس اختبار لغة.
          </div>
        </div>

        <h2 style={s.h2}><ShieldCheck size={16} style={{ verticalAlign: "-3px" }} /> Rules · قواعد التجربة</h2>
        <ul style={s.ul}>
          <li>All data is fictional — no real customer data. <span style={{ color: "#9aa4b1" }}>· جميع البيانات افتراضية، لا توجد بيانات عملاء حقيقيين.</span></li>
          <li>You will work on one assigned task card. <span style={{ color: "#9aa4b1" }}>· ستعمل على بطاقة مهمّة واحدة فقط.</span></li>
          <li>Switch between two tabs: Training Portal &amp; Visa Application Demo. <span style={{ color: "#9aa4b1" }}>· تنقّل بين تبويبَين: Training Portal و Visa Application Demo.</span></li>
          <li>Do not contact any customer. There is no customer phone or email. <span style={{ color: "#9aa4b1" }}>· لا تتواصل مع أي عميل. لا يوجد رقم جوال ولا بريد عميل.</span></li>
          <li>Match the biometric city exactly — never change it. <span style={{ color: "#9aa4b1" }}>· طابِق مدينة البصمة تماماً ولا تغيّرها.</span></li>
        </ul>

        <h2 style={s.h2}><AlertTriangle size={16} style={{ verticalAlign: "-3px", color: "#fca5a5" }} /> Please avoid · تجنّب</h2>
        <ul style={{ ...s.ul, color: "#fca5a5" }}>
          <li>Refreshing or closing the tab. <span style={{ color: "#cbb0b0" }}>· تحديث الصفحة أو إغلاق التبويب.</span></li>
          <li>Switching to other tabs during the test. <span style={{ color: "#cbb0b0" }}>· التنقّل لتبويبات أخرى أثناء الاختبار.</span></li>
          <li>30-minute time limit • 3 violations end the session. <span style={{ color: "#cbb0b0" }}>· مدة 30 دقيقة • 3 تنبيهات تنهي الجلسة.</span></li>
        </ul>
        {err && <div style={s.err}>{err}</div>}
        <button onClick={() => setPhase("board")} style={s.cta} data-testid="goto-board">
          View task board · ابدأ التجربة <ChevronRight size={16} style={{ verticalAlign: "-3px" }} />
        </button>
      </Shell>
    );
  }

  // ── BOARD (5 cards) ──
  if (phase === "board") {
    return (
      <Shell isPreview={isPreview} wide>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h1 style={s.h1}>Training Tasks Assigned to You</h1>
          <span style={{ fontSize: 12, color: "#9a9a9f" }}>{tasks.length} tasks</span>
        </div>
        {err && <div style={s.err}>{err}</div>}
        <div style={s.cardsGrid} data-testid="cards-grid">
          {(() => {
            // The candidate gets ONE active task at a time. The "New" task
            // (or first by lowest countdown days) is openable. Others are
            // locked to teach the candidate that tasks arrive one by one.
            //
            // Admin preview override: ?force_task=<scenario_id> lets a
            // reviewer audit every scenario's Step-3 fields without minting
            // a new session token each time. Has no effect on real candidates.
            let activeIdx = tasks.findIndex(t => countdownInfo(t.travel_date).label === "New");
            if (activeIdx === -1) {
              // fallback: pick the one closest to travel_date but not overdue
              let best = -1, bestDays = Infinity;
              tasks.forEach((t, i) => {
                const d = countdownInfo(t.travel_date).days;
                if (d != null && d >= 0 && d < bestDays) { best = i; bestDays = d; }
              });
              activeIdx = best === -1 ? 0 : best;
            }
            if (isPreview) {
              const forced = new URLSearchParams(window.location.search).get("force_task");
              if (forced) {
                const fIdx = tasks.findIndex(t => t.scenario_id === forced);
                if (fIdx >= 0) activeIdx = fIdx;
              }
            }
            return tasks.map((t, i) => (
              <TaskCard key={t.task_number} task={t} onOpen={openTask}
                        isActive={i === activeIdx} />
            ));
          })()}
        </div>
      </Shell>
    );
  }

  // ── RESULT ──
  if (phase === "result" && result) return <ResultScreen result={result} isPreview={isPreview} isDemoShare={isDemoShare} token={token} />;

  // ── WORK (two-tab) ──
  if (!pickedTask) return null;
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");
  const timeLow = secondsLeft < 60;
  return (
    <div style={s.appWrap}>
      <ResponsiveStyles />
      <DeviceNotice />
      {isPreview && <div style={s.previewBar}>Admin preview — results are not saved, anti-cheat disabled.</div>}
      <div style={s.appHeader} data-testid="app-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={s.taskCode}>{pickedTask.task_number}</span>
          <span style={{ color: "#9a9a9f", fontSize: 12 }}>{pickedTask.country} · {pickedTask.visa_type}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ ...s.statPill, color: warnings >= 2 ? "#ef4444" : "#fbbf24" }} data-testid="warnings-counter">
            <AlertTriangle size={12} /> {warnings}/{MAX_WARNINGS}
          </span>
          <span style={{ ...s.statPill, color: timeLow ? "#ef4444" : "#d4af37", fontVariantNumeric: "tabular-nums" }} data-testid="training-timer">
            <Clock size={12} /> {mm}:{ss}
          </span>
        </div>
      </div>

      <div style={s.tabsHeader}>
        <button onClick={() => setActiveTab("portal")} style={activeTab === "portal" ? s.tabActive : s.tab} data-testid="tab-portal">
          <FileText size={14} style={{ verticalAlign: "-2px", marginInlineEnd: 4 }} /> Training Portal
        </button>
        <button onClick={() => setActiveTab("demo")} style={activeTab === "demo" ? s.tabActive : s.tab} data-testid="tab-demo">
          <ExternalLink size={14} style={{ verticalAlign: "-2px", marginInlineEnd: 4 }} /> Visa Application Demo
        </button>
        {isPreview && (
          <button onClick={() => setAdminArabic(a => !a)} style={{ ...s.tab, marginInlineStart: 8 }}
                  data-testid="admin-translate-toggle" title="Admin only — toggle Arabic for review">
            <Languages size={14} style={{ verticalAlign: "-2px", marginInlineEnd: 4 }} />
            {adminArabic ? "EN" : "AR"} (admin)
          </button>
        )}
        <button onClick={finalize} style={s.finishBtn} data-testid="finalize-btn">
          <Upload size={14} style={{ verticalAlign: "-2px", marginInlineEnd: 4 }} /> Finalize & Submit
        </button>
      </div>

      <div style={s.tabBody} data-testid="tab-body">
        {activeTab === "portal"
          ? <TrainingPortal task={pickedTask} missing={missing} setMissing={setMissing}
                            notes={notes} setNotes={setNotes} pickedSlots={pickedSlots} noSlots={noSlots}
                            pdfGenerated={pdfGenerated} pdfUploaded={pdfUploaded}
                            setPdfUploaded={setPdfUploaded} proofFileName={proofFileName}
                            adminArabic={isPreview && adminArabic} />
          : <VisaApplicationDemo task={pickedTask} demo={demo} setDemo={setDemo}
                                 appointments={apptSlots} pickedSlots={pickedSlots} setPickedSlots={setPickedSlots}
                                 noSlots={noSlots} setNoSlots={setNoSlots}
                                 wizardStep={wizardStep} setWizardStep={setWizardStep}
                                 signedIn={signedIn} setSignedIn={setSignedIn}
                                 onWarning={bumpWarning}
                                 pdfGenerated={pdfGenerated} onGeneratePdf={generateConfirmationPdf}
                                 adminArabic={isPreview && adminArabic} />
        }
      </div>
    </div>
  );
}

function RoadmapStep({ Icon, title }) {
  return (
    <div style={s.roadmapStep}>
      <div style={s.roadmapIcon}><Icon size={22} color="#d4af37" /></div>
      <div style={{ fontSize: 12, color: "#cbd5e1", textAlign: "center" }}>{title}</div>
    </div>
  );
}

function TaskCard({ task, onOpen, isActive, lockReason }) {
  const ci = countdownInfo(task.travel_date);
  const badge = SPECIAL_BADGES[task.scenario_id] || { text: "", color: "#888" };
  const locked = !isActive;
  return (
    <div style={{ ...s.card, opacity: locked ? 0.55 : 1 }} data-testid={`card-${task.scenario_id}`}>
      <div style={s.cardHead}>
        <span style={s.cardCode}>{task.task_number}</span>
        <span style={{ ...s.cardBadge, color: ci.color, borderColor: ci.color }}>{ci.label}</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", margin: "4px 0" }}>{SCENARIO_LABELS[task.scenario_id] || task.country}</div>
      <div style={{ fontSize: 11, color: "#9a9a9f", marginBottom: 10 }}>{task.visa_type}</div>

      <div style={s.cardRow}><Globe size={12} color="#9a9a9f" /> <span>{task.country}</span></div>
      <div style={s.cardRow}><MapPin size={12} color="#9a9a9f" /> <span>Biometric city: <b style={{ color: "#d4af37" }}>{CITY[task.biometric_city]}</b></span></div>
      <div style={s.cardRow}><Plane size={12} color="#9a9a9f" /> <span>Travel: {task.travel_date}</span></div>
      <div style={s.cardRow}><Users size={12} color="#9a9a9f" /> <span>{task.members?.length} traveller(s)</span></div>
      {task.sponsor_display_name && (
        <div style={s.cardRow}><Briefcase size={12} color="#9a9a9f" /> <span>Sponsor: {task.sponsor_display_name} ({task.sponsor_nationality || "SAU"})</span></div>
      )}

      <div style={s.cardBadges}>
        <span style={{ ...s.cardSmallBadge, background: badge.color }}>{badge.text}</span>
        <span style={s.cardSmallBadge}>{locked ? "Queued" : "Assigned"}</span>
      </div>

      <div style={s.progressBar}><div style={{ ...s.progressFill, width: locked ? "0%" : "10%" }} /></div>
      <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>{locked ? "Pending — awaiting prior task completion" : "Progress: 10% · Not started"}</div>

      {locked ? (
        <div style={s.cardLockedBtn} data-testid={`locked-${task.scenario_id}`}>
          Locked — only one active task at a time
        </div>
      ) : (
        <button onClick={() => onOpen(task)} style={s.cardOpenBtn} data-testid={`open-${task.scenario_id}`}>
          Open task <ChevronRight size={14} style={{ verticalAlign: "-2px" }} />
        </button>
      )}
    </div>
  );
}

// ════════════════════ TRAINING PORTAL TAB ════════════════════
function TrainingPortal({ task, missing, setMissing, notes, setNotes, pickedSlots, noSlots,
                          pdfGenerated, pdfUploaded, setPdfUploaded, proofFileName, adminArabic }) {
  const [inner, setInner] = useState("data");
  const [showMissing, setShowMissing] = useState(false);
  const [showNote, setShowNote] = useState(false);
  return (
    <div style={s.portal} data-testid="portal-grid">
      <div style={s.portalTabs} data-testid="portal-tabs">
        {[["data", "Task data"], ["travelers", "Travellers"], ["extracted", "Extracted text"],
          ["appointment", "Appointment"], ["notes", "Notes"]].map(([k, l]) => (
          <button key={k} onClick={() => setInner(k)} style={inner === k ? s.innerTabActive : s.innerTab}
                  data-testid={`ptab-${k}`}>{l}</button>
        ))}
      </div>

      <div style={s.portalBody}>
        {inner === "data" && (
          <div>
            <Row label="Task ID" value={task.task_number} />
            <Row label="Country" value={task.country} />
            <Row label="Visa Type" value={task.visa_type} />
            <Row label="Biometric City" value={CITY[task.biometric_city]} highlight />
            <Row label="Travel Date" value={task.travel_date} />
            <Row label="Return Date" value={task.return_date} />
            <Row label="Travellers" value={task.members?.length} />
            <Row label="Case Type" value={task.applicant_type} />
            {task.sponsor_display_name && <Row label="Sponsor" value={`${task.sponsor_display_name} (${task.sponsor_nationality || "SAU"})`} />}
            {task.operational_flags && (
              <div style={s.flagBox}><AlertTriangle size={14} /> Flags: {task.operational_flags.join(" · ")}</div>
            )}
          </div>
        )}

        {inner === "travelers" && (
          <table style={s.table}>
            <thead><tr><th>#</th><th>Display Name</th><th>Nationality</th><th>Role</th><th>Job</th></tr></thead>
            <tbody>
              {task.members.map((m, i) => (
                <tr key={m.id} style={m.is_companion ? { background: "#1c1a10" } : null}>
                  <td>{i + 1}</td>
                  <td>{m.display_name || m.full_name}</td>
                  <td><b style={{ color: "#d4af37" }}>{m.nationality}</b></td>
                  <td>{m.relationship_to_main}{m.is_companion ? " (companion)" : ""}</td>
                  <td>{m.job_title || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {inner === "extracted" && (
          <div>
            {task.members.map(m => (
              <div key={m.id} style={s.docBlock}>
                <div style={s.docTitle}>{m.display_name || m.full_name} — Passport extract</div>
                <pre style={s.pre} data-testid={`passport-${m.id}`}>
{`Name        : ${m.display_name || m.full_name}
Passport No : ${m.passport_number}
Nationality : ${m.nationality}
DOB         : ${m.date_of_birth}
Expiry      : ${m.expiry_date}
Sex         : ${m.sex}`}
                </pre>
                {m.nationality !== "SAU" && (
                  <>
                    <div style={{ ...s.docTitle, marginTop: 8 }}>Iqama extract</div>
                    <pre style={s.pre}>
{`Iqama No    : ${m.national_id}
Nationality : ${m.nationality}${m.iqama_expiry ? `\nExpiry      : ${m.iqama_expiry}` : ""}`}
                    </pre>
                  </>
                )}
              </div>
            ))}
            {task.sponsor_display_name && (
              <div style={s.docBlock}>
                <div style={s.docTitle}>Sponsor</div>
                <pre style={s.pre}>{`Sponsor     : ${task.sponsor_display_name}\nNationality : ${task.sponsor_nationality || "SAU"}`}</pre>
              </div>
            )}
          </div>
        )}

        {inner === "appointment" && (
          <div>
            <p style={{ color: "#cbd5e1", fontSize: 13 }}>{adminArabic ? "افتح تبويب Visa Application Demo، اقترح المواعيد، ولّد PDF، ثم ارجع هنا وارفعه كإثبات." : "Open the Visa Application Demo tab, propose appointments, generate the PDF, then return here and attach it as proof."}</p>
            {pickedSlots.length ? (
              <table style={s.table}>
                <thead><tr><th>#</th><th>Date</th><th>Time</th><th>City</th><th>Center</th><th>Fee</th></tr></thead>
                <tbody>{pickedSlots.map((sl, i) => (
                  <tr key={sl.id}><td>{i + 1}</td><td>{sl.date}</td><td>{sl.time}</td>
                    <td>{CITY[sl.city]}</td><td>{sl.center_type}</td><td>{sl.fee} SAR</td></tr>))}</tbody>
              </table>
            ) : noSlots ? <div style={{ color: "#fbbf24", fontSize: 13 }}>Marked: no suitable appointments available.</div>
              : <div style={{ color: "#888", fontSize: 13 }}>No appointment recorded yet.</div>}

            <div style={{ marginTop: 16, padding: 14, background: "#0f0f17", border: "1px solid #2a2a3a", borderRadius: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Upload size={14} color="#d4af37" />
                <b style={{ color: "#d4af37" }}>Attach appointment confirmation (PDF)</b>
              </div>
              {!pdfGenerated && (
                <div style={{ fontSize: 12, color: "#fbbf24" }}>Generate the PDF in Visa Application Demo → Step 5 first.</div>
              )}
              {pdfGenerated && !pdfUploaded && (
                <div>
                  <div style={{ fontSize: 12, color: "#cbd5e1", marginBottom: 8 }}>Attach the downloaded file: <b>{proofFileName}</b></div>
                  <input type="file" accept="application/pdf"
                         onChange={(e) => {
                           const f = e.target.files?.[0];
                           if (!f) return;
                           const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");
                           if (!isPdf) {
                             alert("Only PDF files are accepted. Please attach the downloaded PDF.");
                             e.target.value = "";
                             return;
                           }
                           setPdfUploaded(true);
                         }}
                         data-testid="upload-pdf-input" />
                  <div style={{ fontSize: 11, color: "#9a9a9f", marginTop: 6 }}>Only <b>application/pdf</b> is accepted. Images and other formats are rejected.</div>
                </div>
              )}
              {pdfUploaded && (
                <div style={{ fontSize: 12, color: "#10b981", display: "flex", alignItems: "center", gap: 6 }}>
                  <Upload size={12} /> {proofFileName} attached as appointment proof.
                </div>
              )}
            </div>
          </div>
        )}

        {inner === "notes" && (
          <div>
            {missing.length === 0 && notes.length === 0 && <div style={{ color: "#888", fontSize: 13 }}>No notes yet.</div>}
            {missing.map((m, i) => (
              <div key={i} style={s.noteCard}>
                <span style={s.noteTag}>Missing data</span>
                <b>{m.field_name}</b> — {m.description}
              </div>
            ))}
            {notes.map((n, i) => (
              <div key={i} style={s.noteCard}>
                <span style={{ ...s.noteTag, background: n.severity === "blocker" ? "#7f1d1d" : "#1f2937" }}>{n.severity}</span>
                {n.text}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={s.portalActions}>
        <button onClick={() => setShowMissing(true)} style={s.actBtn} data-testid="open-missing">
          <AlertTriangle size={13} /> Request missing ({missing.length})
        </button>
        <button onClick={() => setShowNote(true)} style={s.actBtn} data-testid="open-note">
          <FileText size={13} /> Operational note ({notes.length})
        </button>
      </div>

      {showMissing && <MissingModal onClose={() => setShowMissing(false)}
                                      onSave={(item) => { setMissing([...missing, item]); setShowMissing(false); }} />}
      {showNote && <NoteModal onClose={() => setShowNote(false)}
                              onSave={(item) => { setNotes([...notes, item]); setShowNote(false); }} />}
    </div>
  );
}
function MissingModal({ onClose, onSave }) {
  const [field, setField] = useState(""); const [desc, setDesc] = useState("");
  return (
    <Modal title="Request missing data" onClose={onClose}>
      <label style={s.label}>Missing field</label>
      <select value={field} onChange={e => setField(e.target.value)} style={s.input} data-testid="missing-field-sel">
        <option value="">— Select —</option><option>iqama</option><option>exit_reentry_visa</option><option>bil_number</option>
        <option>sponsor_letter</option><option>passport_copy</option><option>insurance</option>
        <option>flight_reservation</option><option>hotel_reservation</option>
      </select>
      <label style={s.label}>Description</label>
      <textarea value={desc} onChange={e => setDesc(e.target.value)} style={{ ...s.input, height: 70 }} data-testid="missing-desc-ta" />
      <button onClick={() => onSave({ field_name: field, description: desc })} disabled={!field || desc.length < 4}
              style={s.cta} data-testid="save-missing">Save</button>
    </Modal>
  );
}
function NoteModal({ onClose, onSave }) {
  const [text, setText] = useState(""); const [sev, setSev] = useState("info");
  return (
    <Modal title="Operational note" onClose={onClose}>
      <label style={s.label}>Severity</label>
      <select value={sev} onChange={e => setSev(e.target.value)} style={s.input}>
        <option value="info">Info</option><option value="warning">Warning</option><option value="blocker">Blocker</option>
      </select>
      <label style={s.label}>Note text</label>
      <textarea value={text} onChange={e => setText(e.target.value)} style={{ ...s.input, height: 80 }} data-testid="note-ta" />
      <button onClick={() => onSave({ text, severity: sev })} disabled={text.length < 4} style={s.cta} data-testid="save-note">Save</button>
    </Modal>
  );
}

// ════════════════════ VISA APPLICATION DEMO TAB ════════════════════
function VisaApplicationDemo({ task, demo, setDemo, appointments, pickedSlots, setPickedSlots,
                                noSlots, setNoSlots, wizardStep, setWizardStep,
                                signedIn, setSignedIn, onWarning,
                                pdfGenerated, onGeneratePdf, adminArabic }) {
  const set = (k, v) => setDemo({ ...demo, [k]: v });
  // The expected training email for THIS task. Pure mock — nothing leaves the browser.
  const expectedEmail = `${(task.task_number || "task").toLowerCase()}@training.demo`;
  const STEPS = [
    "0. Sign In",
    "1. Application Type",
    "2. Applicant Details",
    "3. Sponsor / Iqama / Special Case",
    "4. Appointment Search",
    "5. Confirmation",
  ];
  const next = () => {
    // Cannot leave Step 0 until signed in
    if (wizardStep === 0 && !signedIn) { return; }
    setWizardStep(s => Math.min(5, s + 1));
  };
  const back = () => setWizardStep(s => Math.max(0, s - 1));

  return (
    <div style={s.demo}>
      <div style={s.demoBrand}>VISA APPLICATION DEMO · APPLICATION BOOKING SIMULATION</div>
      <div style={{ marginTop: 10, marginBottom: 10, padding: 10, background: "#0f1a2e", border: "1px solid #1e3a5f", borderRadius: 8, fontSize: 11, color: "#93c5fd" }}>
        {adminArabic
          ? "ملاحظة: قد تُرسل مراكز المواعيد تحديثات عبر البريد. استخدم البريد التدريبي الخاص بالمهمة فقط، ولا تستخدم أي بريد عميل حقيقي."
          : "Note: Some appointment centers send updates by email. Use the training email from the task and follow email instructions. No real customer email is used."}
      </div>

      {/* Wizard stepper */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16, paddingBottom: 10, borderBottom: "1px solid #2a2a3a" }}>
        {STEPS.map((label, i) => {
          const n = i; const active = wizardStep === n; const done = wizardStep > n;
          return (
            <div key={n} style={{
              padding: "5px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: active ? "#d4af37" : done ? "#10b981" : "#1a1a25",
              color: active ? "#0a0a0f" : done ? "#fff" : "#9a9a9f",
              border: "1px solid " + (active ? "#d4af37" : done ? "#10b981" : "#2a2a3a"),
            }}>{label}</div>
          );
        })}
      </div>

      {wizardStep === 0 && (
        <SignInStep task={task} expectedEmail={expectedEmail}
                    demo={demo} setDemo={setDemo}
                    signedIn={signedIn} setSignedIn={setSignedIn}
                    onWarning={onWarning} adminArabic={adminArabic} />
      )}

      {wizardStep === 1 && (
        <FormSection title={adminArabic ? "الخطوة 1 — نوع الطلب" : "Step 1 — Application Type"}>
          <FieldDropdown name="destination_country" label="Destination Country" value={demo.destination_country} onChange={v => set("destination_country", v)}
                         options={["Turkey", "France", "Germany", "Spain", "Italy", "Canada", "UK"]} />
          <FieldDropdown name="visa_type" label="Visa Type" value={demo.visa_type} onChange={v => set("visa_type", v)}
                         options={["Tourism", "Business", "Family Visit", "Domestic Worker", "Student", "Transit"]} />
          <FieldDropdown name="purpose" label="Purpose of Travel" value={demo.purpose} onChange={v => set("purpose", v)}
                         options={["Holiday", "Business meeting", "Family visit", "Employment-related", "Medical"]} />
          <FieldDropdown name="applicant_type" label="Applicant Type" value={demo.applicant_type} onChange={v => set("applicant_type", v)}
                         options={["Individual", "Family", "Group", "Domestic Worker"]} />
          <FieldDropdown name="entries" label="Number of Entries" value={demo.entries} onChange={v => set("entries", v)} options={["Single", "Double", "Multiple"]} />
          <FieldText name="num_travelers" label="Number of Travellers" value={demo.num_travelers} onChange={v => set("num_travelers", v)} />
        </FormSection>
      )}

      {wizardStep === 2 && (
        <FormSection title={adminArabic ? "الخطوة 2 — بيانات المتقدّم" : "Step 2 — Applicant Details"}>
          <FieldRadio name="is_saudi" label="Is the applicant Saudi national?" value={demo.is_saudi} onChange={v => set("is_saudi", v)} options={[["yes", "Yes"], ["no", "No"]]} />
          <FieldRadio name="is_resident" label="Resident in Saudi Arabia?" value={demo.is_resident} onChange={v => set("is_resident", v)} options={[["yes", "Yes"], ["no", "No"]]} />
          <FieldDropdown name="active_applicant" label="Active applicant on this file" value={demo.active_applicant} onChange={v => set("active_applicant", v)}
                         options={["Main traveler", "Spouse", "Child", "Domestic Worker", "Other"]} />
          <FieldText name="first_name" label="First Name" value={demo.first_name} onChange={v => set("first_name", v)} />
          <FieldText name="last_name" label="Last Name" value={demo.last_name} onChange={v => set("last_name", v)} />
          <FieldDate name="dob" label="Date of Birth" value={demo.dob} onChange={v => set("dob", v)} />
          <FieldDropdown name="nationality" label="Nationality" value={demo.nationality} onChange={v => set("nationality", v)}
                         options={["SAU", "PAK", "IND", "PHL", "LKA", "EGY", "NPL", "BGD", "IDN", "OTHER"]} />
          <FieldText name="passport_number" label="Passport Number" value={demo.passport_number} onChange={v => set("passport_number", v)} />
          <FieldDate name="passport_issue" label="Passport Issue Date" value={demo.passport_issue} onChange={v => set("passport_issue", v)} />
          <FieldDate name="passport_expiry" label="Passport Expiry Date" value={demo.passport_expiry} onChange={v => set("passport_expiry", v)} />
          <FieldText name="contact_email" label="Notification Email (training email — appointment updates may be sent here)" value={demo.contact_email} onChange={v => set("contact_email", v)} />
        </FormSection>
      )}

      {wizardStep === 3 && (
        <FormSection title={adminArabic ? "الخطوة 3 — الجواز والهوية / الإقامة / الكفيل" : "Step 3 — Passport, ID, Iqama & Sponsor"}>
          <DynamicIdentityFields task={task} demo={demo} set={set} adminArabic={adminArabic} />
        </FormSection>
      )}

      {wizardStep === 4 && (
        <FormSection title={adminArabic ? "الخطوة 4 — البحث عن المواعيد" : "Step 4 — Appointment Search"}>
          <FieldDropdown name="biometric_city" label="Biometric City" value={demo.biometric_city} onChange={v => set("biometric_city", v)} options={["riyadh", "jeddah", "dammam"]} />
          <FieldDropdown name="application_center_type" label="Application Center Type" value={demo.application_center_type} onChange={v => set("application_center_type", v)} options={["Standard", "Premium", "Outsourcing"]} />
          <p style={{ gridColumn: "1 / -1", color: "#9a9a9f", fontSize: 12, marginTop: 6 }}>Customer-assigned city: <b style={{ color: "#d4af37" }}>{CITY[task.biometric_city]}</b> — do not choose a different one.</p>
          {noSlots ? (
            <div style={{ ...s.noSlotsBox, gridColumn: "1 / -1" }} data-testid="no-slots-banner">
              <div style={{ color: "#fbbf24", fontWeight: 800, fontSize: 13 }}>No suitable appointments available</div>
              <div style={{ fontSize: 12, marginTop: 6, color: "#cbd5e1" }}>Add an operational note in the Training Portal.</div>
            </div>
          ) : (
            <div style={{ gridColumn: "1 / -1" }}>
              <p style={{ fontSize: 12, color: "#cbd5e1" }}>Select up to 5 candidate appointments (city must match):</p>
              <div style={s.apptTableWrap}>
                <table style={s.apptTable}>
                  <thead><tr><th>Select</th><th>Date</th><th>Time</th><th>City</th><th>Center</th><th>Availability</th><th>Fee</th></tr></thead>
                  <tbody>{appointments.map(slot => {
                    const picked = pickedSlots.some(p => p.id === slot.id);
                    const cityMatch = slot.city === task.biometric_city;
                    return (
                      <tr key={slot.id} style={!cityMatch ? { opacity: 0.55 } : null}>
                        <td><input type="checkbox" checked={picked} disabled={!cityMatch || (!picked && pickedSlots.length >= 5)}
                          onChange={() => setPickedSlots(picked ? pickedSlots.filter(p => p.id !== slot.id) : [...pickedSlots, slot])}
                          data-testid={`appt-pick-${slot.id}`} /></td>
                        <td>{slot.date}</td><td>{slot.time}</td><td>{CITY[slot.city]}</td>
                        <td>{slot.center_type}</td><td>{slot.available ? "Available" : "Full"}</td><td>{slot.fee} SAR</td>
                      </tr>);
                  })}</tbody>
                </table>
              </div>
              <button onClick={() => setNoSlots(true)} style={{ ...s.actBtn, marginTop: 10, background: "#7f1d1d", borderColor: "#7f1d1d" }}
                      data-testid="mark-no-slots">No suitable appointments available</button>
            </div>
          )}
          <textarea value={demo.operational_note || ""} onChange={e => set("operational_note", e.target.value)}
                    style={{ ...s.input, height: 70, gridColumn: "1 / -1" }}
                    placeholder="Operational note for this appointment search (optional)" data-testid="demo-op-note" />
        </FormSection>
      )}

      {wizardStep === 5 && (
        <FormSection title={adminArabic ? "الخطوة 5 — تأكيد الموعد" : "Step 5 — Confirmation"}>
          <div style={{ gridColumn: "1 / -1", padding: 16, background: "#0f0f17", border: "1px solid #2a2a3a", borderRadius: 10 }}>
            <h4 style={{ margin: 0, color: "#d4af37" }}>{adminArabic ? "توليد PDF تأكيد الموعد (تجريبي)" : "Generate Demo Appointment Confirmation"}</h4>
            <p style={{ fontSize: 12, color: "#cbd5e1", marginTop: 8, lineHeight: 1.7 }}>
              {adminArabic
                ? "اضغط أدناه لتوليد ملف PDF حقيقي يحتوي على بيانات الموعد التي اقترحتها. ثم ارجع إلى Training Portal → تبويب الموعد وارفع الملف كإثبات. الطلب لا يكتمل بدون توليد PDF ورفعه."
                : "Click below to generate a real PDF with the appointment details you proposed. Then go back to the Training Portal → Appointment tab and attach it as proof. The application is not considered complete without the PDF generated and uploaded."}
            </p>
            <div style={{ marginTop: 12, padding: 12, background: "#13131a", borderRadius: 8, fontSize: 12 }}>
              <div><b>Applicant:</b> {demo.first_name || "—"} {demo.last_name || ""}</div>
              <div><b>Passport:</b> {demo.passport_number || "—"}</div>
              <div><b>Country:</b> {task.country} · <b>City:</b> {(demo.biometric_city || task.biometric_city).toUpperCase()}</div>
              <div><b>Slots picked:</b> {pickedSlots.length}{noSlots ? " (No slots flagged)" : ""}</div>
              <div><b>Email:</b> {demo.contact_email || "—"}</div>
            </div>
            <button onClick={onGeneratePdf} disabled={pdfGenerated}
                    style={{ ...s.cta, marginTop: 14, background: pdfGenerated ? "#374151" : "#d4af37", color: pdfGenerated ? "#9a9a9f" : "#0a0a0f" }}
                    data-testid="generate-pdf-btn">
              <FileDown size={14} style={{ verticalAlign: "-2px", marginInlineEnd: 6 }} />
              {pdfGenerated ? "PDF Generated ✓" : "Generate Demo Confirmation PDF"}
            </button>
            {pdfGenerated && (
              <p style={{ marginTop: 10, fontSize: 12, color: "#10b981" }}>
                PDF downloaded. Now switch to Training Portal → Appointment tab and attach the file.
              </p>
            )}
          </div>
        </FormSection>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, paddingTop: 14, borderTop: "1px solid #2a2a3a" }}>
        <button onClick={back} disabled={wizardStep === 0} style={{ ...s.actBtn, opacity: wizardStep === 0 ? 0.5 : 1 }} data-testid="wizard-back">
          <ChevronLeft size={14} /> Back
        </button>
        <span style={{ fontSize: 12, color: "#9a9a9f" }}>Step {wizardStep} / 5</span>
        <button onClick={next}
                disabled={wizardStep === 5 || (wizardStep === 0 && !signedIn)}
                style={{ ...s.actBtn, background: "#d4af37", color: "#0a0a0f", border: "none", opacity: (wizardStep === 5 || (wizardStep === 0 && !signedIn)) ? 0.5 : 1 }} data-testid="wizard-next">
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

// ──────────── Step 0: Sign In / Create Demo Application Account ────────────
// Pure mock account creation. No real email is sent. The candidate MUST use
// the training email that belongs to this task; using a different email
// triggers a behavioural warning (anti-cheat).
function SignInStep({ task, expectedEmail, demo, setDemo, signedIn, setSignedIn, onWarning, adminArabic }) {
  const [email, setEmail] = useState(demo.contact_email || expectedEmail);
  // Persist the prefilled training email into wizard state so downstream steps
  // (Step 5 PDF, finalize gate) see it even before the user touches the field.
  useEffect(() => {
    if (!demo.contact_email && expectedEmail) {
      setDemo({ ...demo, contact_email: expectedEmail });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedEmail]);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [mismatchFlagged, setMismatchFlagged] = useState(false);
  const SIM_OTP = "123456"; // simulated only — printed on screen for clarity

  const t = adminArabic ? {
    title: "الخطوة 0 — تسجيل الدخول / إنشاء حساب لطلب التأشيرة (تجريبي)",
    hint: "استخدم البريد التدريبي المرتبط بهذه المهمة فقط. لا يُستخدم بريد عميل حقيقي. كل العملية محاكاة.",
    expectedLabel: "البريد التدريبي للمهمة",
    emailLabel: "بريد المتقدّم (يجب أن يطابق البريد التدريبي)",
    sendOtp: "إرسال رمز تحقّق (محاكاة)",
    otpHint: "تمت محاكاة الإرسال. الرمز التجريبي للعرض:",
    otpLabel: "أدخل رمز التحقّق",
    verify: "تحقّق",
    signedIn: "تم تسجيل الدخول التجريبي بنجاح.",
    wrongEmail: "البريد لا يطابق البريد التدريبي. لا يُسمح باستخدام بريد آخر.",
    wrongOtp: "رمز التحقّق غير صحيح.",
  } : {
    title: "Step 0 — Sign In / Create Demo Application Account",
    hint: "Use only the training email tied to this task. No real customer email is used — everything here is simulated.",
    expectedLabel: "Training email for this task",
    emailLabel: "Applicant email (must match the training email)",
    sendOtp: "Send verification code (simulated)",
    otpHint: "Send simulated. Demo code shown for evaluation:",
    otpLabel: "Enter verification code",
    verify: "Verify",
    signedIn: "Demo sign-in successful.",
    wrongEmail: "Email does not match the training email. You cannot use a different email.",
    wrongOtp: "Invalid verification code.",
  };

  const handleSendOtp = () => {
    setError("");
    if (!email || email.trim().toLowerCase() !== expectedEmail) {
      setError(t.wrongEmail);
      if (!mismatchFlagged) { setMismatchFlagged(true); onWarning && onWarning(); }
      return;
    }
    setOtpSent(true);
  };
  const handleVerify = () => {
    setError("");
    if (otp.trim() !== SIM_OTP) { setError(t.wrongOtp); return; }
    setDemo({ ...demo, contact_email: email });
    setSignedIn(true);
  };

  return (
    <div style={s.formSection}>
      <h3 style={s.formTitle}>{t.title}</h3>
      <p style={{ fontSize: 12, color: "#cbd5e1", marginTop: -4, marginBottom: 10 }}>{t.hint}</p>
      <div style={{ ...s.formGrid }}>
        <div style={{ ...s.field, gridColumn: "1 / -1" }}>
          <label style={s.fieldLabel}>{t.expectedLabel}</label>
          <div style={{ ...s.input, background: "#0a0a0f", color: "#d4af37", fontWeight: 800, letterSpacing: 0.3 }} data-testid="signin-expected-email">
            {expectedEmail}
          </div>
        </div>
        <div style={{ ...s.field, gridColumn: "1 / -1" }}>
          <label style={s.fieldLabel}>{t.emailLabel}</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} disabled={signedIn}
                 style={s.input} placeholder={expectedEmail} data-testid="signin-email-input" />
        </div>
        {!signedIn && !otpSent && (
          <div style={{ gridColumn: "1 / -1" }}>
            <button onClick={handleSendOtp} style={{ ...s.actBtn, background: "#d4af37", color: "#0a0a0f", border: "none" }}
                    data-testid="signin-send-otp">{t.sendOtp}</button>
          </div>
        )}
        {!signedIn && otpSent && (
          <>
            <div style={{ ...s.field, gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 12, color: "#10b981", marginBottom: 6 }}>{t.otpHint} <b style={{ color: "#fbbf24" }}>{SIM_OTP}</b></div>
              <label style={s.fieldLabel}>{t.otpLabel}</label>
              <input value={otp} onChange={(e) => setOtp(e.target.value)} style={s.input}
                     placeholder="123456" data-testid="signin-otp-input" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <button onClick={handleVerify} style={{ ...s.actBtn, background: "#10b981", color: "#fff", border: "none" }}
                      data-testid="signin-verify">{t.verify}</button>
            </div>
          </>
        )}
        {signedIn && (
          <div style={{ gridColumn: "1 / -1", padding: 10, background: "#0f2718", border: "1px solid #10b981", borderRadius: 8, color: "#10b981", fontSize: 13 }}
               data-testid="signin-success">
            ✓ {t.signedIn} <span style={{ color: "#9a9a9f", marginInlineStart: 8 }}>({expectedEmail})</span>
          </div>
        )}
        {error && <div style={{ gridColumn: "1 / -1", color: "#fca5a5", fontSize: 12 }} data-testid="signin-error">{error}</div>}
      </div>
    </div>
  );
}


function FormSection({ title, children }) { return <div style={s.formSection}><h3 style={s.formTitle}>{title}</h3><div style={s.formGrid} data-testid="wizard-grid">{children}</div></div>; }

// ────────────────────────────────────────────────────────────────────────────
// Step 3 — Dynamic identity fields. Renders ONLY the fields that are relevant
// to the current scenario, so the candidate never sees illogical fields.
//   Scenarios driven by task.applicant_type / task.scenario_id:
//     • individual + nationality=SAU → Saudi solo (passport + national ID)
//     • family → Family application = Yes + per-member passport + national ID
//     • domestic_worker → Passport + Iqama + Exit/Re-entry + Sponsor block
//     • non_saudi_resident → Passport + Iqama + Residence country
//     • ca_bil (Canada) → BIL block; if BIL = No → require Request Missing Data
// ────────────────────────────────────────────────────────────────────────────
function DynamicIdentityFields({ task, demo, set, adminArabic }) {
  // Resolve scenario
  const t = task || {};
  const isCanada = (t.scenario_id === "ca_bil") || (String(t.country || "").toLowerCase() === "canada");
  const isDomestic = t.applicant_type === "domestic_worker";
  const isFamily = t.applicant_type === "family";
  const isResident = t.applicant_type === "non_saudi_resident";
  // Saudi-only main applicant (no Iqama needed). True for fr_individual and most fr_family.
  const mainNationality = (t.members && t.members[0] && t.members[0].nationality) || "SAU";
  const isSaudiSolo = !isDomestic && !isResident && !isFamily && mainNationality === "SAU";

  return (
    <>
      {/* ── Passport block — always shown ── */}
      <div style={{ gridColumn: "1 / -1", padding: 10, background: "rgba(212,175,55,0.04)", borderRadius: 8, border: "1px solid rgba(212,175,55,0.15)" }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#d4af37", marginBottom: 8 }}>
          {adminArabic ? "بيانات الجواز" : "Passport details"}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <FieldText name="passport_number" label="Passport Number" value={demo.passport_number} onChange={v => set("passport_number", v)} />
          <FieldDate name="passport_issue_date" label="Passport Issue Date" value={demo.passport_issue_date} onChange={v => set("passport_issue_date", v)} />
          <FieldDate name="passport_expiry_date" label="Passport Expiry Date" value={demo.passport_expiry_date} onChange={v => set("passport_expiry_date", v)} />
          <FieldDate name="date_of_birth" label="Date of Birth" value={demo.date_of_birth} onChange={v => set("date_of_birth", v)} />
        </div>
      </div>

      {/* ── Identity block ── National ID (Saudi) or Iqama (resident / worker) ── */}
      {(isSaudiSolo || isFamily) && (
        <div style={{ gridColumn: "1 / -1", padding: 10, background: "rgba(16,185,129,0.05)", borderRadius: 8, border: "1px solid rgba(16,185,129,0.2)" }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#10b981", marginBottom: 8 }}>
            {adminArabic ? "بيانات الهوية الوطنية" : "National ID"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <FieldText name="national_id" label="National ID" value={demo.national_id} onChange={v => set("national_id", v)} />
            <FieldText name="nationality" label="Nationality" value={demo.nationality || "SAU"} onChange={v => set("nationality", v)} />
          </div>
          {isFamily && (
            <div style={{ marginTop: 8 }}>
              <FieldRadio name="is_family" label="Family application?" value={demo.is_family || "yes"} onChange={v => set("is_family", v)} options={[["yes", "Yes"], ["no", "No"]]} />
              <p style={{ fontSize: 11, color: "#9a9a9f", marginTop: 4 }}>For each traveller add Passport + National ID in the Training Portal → Members tab.</p>
            </div>
          )}
        </div>
      )}

      {(isDomestic || isResident) && (
        <div style={{ gridColumn: "1 / -1", padding: 10, background: "rgba(59,130,246,0.06)", borderRadius: 8, border: "1px solid rgba(59,130,246,0.25)" }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#60a5fa", marginBottom: 8 }}>
            {adminArabic ? "بيانات الإقامة" : "Iqama details"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <FieldText name="iqama_number" label="Iqama Number" value={demo.iqama_number} onChange={v => set("iqama_number", v)} />
            <FieldDate name="iqama_expiry" label="Iqama Expiry" value={demo.iqama_expiry} onChange={v => set("iqama_expiry", v)} />
            <FieldText name="nationality" label="Nationality" value={demo.nationality} onChange={v => set("nationality", v)} />
            {isResident && <FieldText name="residence_country" label="Residence Country" value={demo.residence_country || "Saudi Arabia"} onChange={v => set("residence_country", v)} />}
            {isDomestic && <FieldCheckbox name="exit_reentry" label="Exit / Re-entry visa available" checked={!!demo.exit_reentry} onChange={v => set("exit_reentry", v)} />}
          </div>
        </div>
      )}

      {/* ── Sponsor block — only when relevant ── */}
      {isDomestic && (
        <div style={{ gridColumn: "1 / -1", padding: 10, background: "rgba(168,85,247,0.06)", borderRadius: 8, border: "1px solid rgba(168,85,247,0.25)" }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#c084fc", marginBottom: 8 }}>
            {adminArabic ? "بيانات الكفيل" : "Sponsor details"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <FieldText name="sponsor_name" label="Sponsor Full Name" value={demo.sponsor_name} onChange={v => set("sponsor_name", v)} />
            <FieldText name="sponsor_id" label="Sponsor National ID" value={demo.sponsor_id} onChange={v => set("sponsor_id", v)} />
            <FieldDropdown name="relationship_to_sponsor" label="Relationship to sponsor"
                           value={demo.relationship_to_sponsor || "Domestic Worker / Kafeel"}
                           onChange={v => set("relationship_to_sponsor", v)}
                           options={["Domestic Worker / Kafeel", "Employee", "Spouse", "Child"]} />
            <FieldCheckbox name="sponsor_letter" label="Sponsor letter available" checked={!!demo.sponsor_letter} onChange={v => set("sponsor_letter", v)} />
          </div>
        </div>
      )}

      {/* ── Canada BIL block ── */}
      {isCanada && (
        <div style={{ gridColumn: "1 / -1", padding: 10, background: "rgba(239,68,68,0.06)", borderRadius: 8, border: "1px solid rgba(239,68,68,0.3)" }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#fca5a5", marginBottom: 8 }}>
            {adminArabic ? "بيانات BIL (كندا)" : "BIL block (Canada)"}
          </div>
          <FieldRadio name="bil_issued" label="BIL issued?" value={demo.bil_issued}
                      onChange={v => set("bil_issued", v)} options={[["yes", "Yes"], ["no", "No"]]} />
          {demo.bil_issued === "yes" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
              <FieldText name="bil_number" label="BIL Number" value={demo.bil_number} onChange={v => set("bil_number", v)} />
              <FieldDate name="bil_issue_date" label="BIL Issue Date" value={demo.bil_issue_date} onChange={v => set("bil_issue_date", v)} />
              <FieldText name="application_reference" label="Application Reference" value={demo.application_reference} onChange={v => set("application_reference", v)} />
            </div>
          )}
          {demo.bil_issued === "no" && (
            <div style={{ marginTop: 10, padding: 10, background: "rgba(239,68,68,0.08)", border: "1px solid #ef4444", borderRadius: 8, fontSize: 12.5, color: "#fca5a5", lineHeight: 1.7 }}
                 data-testid="bil-missing-warning">
              <b>BIL letter not yet received from the Canadian authorities.</b>
              <div style={{ marginTop: 6 }}>You cannot proceed with the appointment booking. Go back to the Training Portal → <b>Missing Documents</b> tab and request the BIL letter before continuing.</div>
            </div>
          )}
        </div>
      )}

      {/* Hint when no extra block applies (pure Saudi solo, non-Canada) */}
      {isSaudiSolo && !isCanada && (
        <p style={{ gridColumn: "1 / -1", fontSize: 11, color: "#9a9a9f" }}>
          {adminArabic ? "متقدّم سعودي فردي — لا حاجة لإقامة، ولا لـ BIL، ولا لبيانات كفيل." : "Saudi individual applicant — no Iqama, no BIL, no Sponsor block required."}
        </p>
      )}
    </>
  );
}


function FieldDropdown({ name, label, value, onChange, options }) {
  return <div style={s.field}><label style={s.fieldLabel}>{label}</label>
    <select value={value || ""} onChange={e => onChange(e.target.value)} style={s.input} data-testid={`f-${name}`}>
      <option value="">— Select —</option>{options.map(o => <option key={o} value={o}>{o}</option>)}
    </select></div>;
}
function FieldText({ name, label, value, onChange }) {
  return <div style={s.field}><label style={s.fieldLabel}>{label}</label>
    <input value={value || ""} onChange={e => onChange(e.target.value)} style={s.input} data-testid={`f-${name}`} /></div>;
}
function FieldDate({ name, label, value, onChange }) {
  return <div style={s.field}><label style={s.fieldLabel}>{label}</label>
    <input type="date" value={value || ""} onChange={e => onChange(e.target.value)} style={s.input} data-testid={`f-${name}`} /></div>;
}
function FieldRadio({ name, label, value, onChange, options }) {
  return <div style={{ ...s.field, gridColumn: "1 / -1" }}><label style={s.fieldLabel}>{label}</label>
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>{options.map(([v, l]) =>
      <label key={v} style={s.radioInline}><input type="radio" checked={value === v} onChange={() => onChange(v)} data-testid={`f-${name}-${v}`} /> {l}</label>)}</div></div>;
}
function FieldCheckbox({ name, label, checked, onChange }) {
  return <div style={s.field}><label style={s.checkInline}><input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} data-testid={`f-${name}`} />{label}</label></div>;
}

// ════════════════════ Helpers / Scoring / Shells ════════════════════
// Pure PDF builder — returns the saved filename. Side effects (download)
// are intentional and isolated to this helper to keep the React component free
// of mutation-heavy logic.
function buildConfirmationPdf(pickedTask, pickedSlots, demo) {
  if (!pickedTask) return null;
  const slot = pickedSlots[0];
  const confNum = `DEMO-${pickedTask.task_number}-${Math.floor(Math.random() * 9000 + 1000)}`;
  const doc = new jsPDF();
  doc.setFontSize(18); doc.setTextColor(40, 40, 40);
  doc.text("Demo Appointment Confirmation", 20, 25);
  doc.setFontSize(10); doc.setTextColor(120, 120, 120);
  doc.text("This is a training simulation — not a real appointment.", 20, 33);
  doc.setDrawColor(212, 175, 55); doc.line(20, 38, 190, 38);
  doc.setFontSize(11); doc.setTextColor(50, 50, 50);
  const lines = [
    ["Confirmation Number", confNum],
    ["Task Reference", pickedTask.task_number],
    ["Country", pickedTask.country],
    ["Visa Type", pickedTask.visa_type],
    ["Applicant Name", demo.first_name && demo.last_name ? `${demo.first_name} ${demo.last_name}` : "Applicant #001"],
    ["Passport Number", demo.passport_number || "—"],
    ["Nationality", demo.nationality || "—"],
    ["Biometric City", (pickedTask.biometric_city || "").toUpperCase()],
    ["Center Type", demo.application_center_type || "Standard"],
    ["Appointment Date", slot ? slot.date : "—"],
    ["Appointment Time", slot ? slot.time : "—"],
    ["Appointment Fee", slot ? `${slot.fee} SAR` : "—"],
    ["Notification Email", demo.contact_email || "demo-applicant@training.example"],
    ["Issued At", new Date().toISOString().slice(0, 19).replace("T", " ")],
  ];
  const startY = 48;
  lines.forEach(([k, v], idx) => {
    const y = startY + idx * 8;
    doc.setFont(undefined, "bold"); doc.text(`${k}:`, 20, y);
    doc.setFont(undefined, "normal"); doc.text(String(v), 75, y);
  });
  const tailY = startY + lines.length * 8;
  doc.setFontSize(9); doc.setTextColor(150, 150, 150);
  doc.text("Please carry a copy of this confirmation on the appointment day.", 20, tailY + 8);
  doc.text("Updates may be sent to the notification email above.", 20, tailY + 14);
  doc.save(`${confNum}.pdf`);
  return `${confNum}.pdf`;
}


function generateSlots(task) {
  const cities = ["riyadh", "jeddah", "dammam"];
  const base = task.travel_date ? new Date(task.travel_date) : new Date();
  base.setDate(base.getDate() - 10);
  const slots = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(base); d.setDate(d.getDate() + i * 2);
    const c = i < 4 ? task.biometric_city : cities.filter(x => x !== task.biometric_city)[i % 2];
    slots.push({
      id: `slot-${i}-${c}`, date: d.toISOString().slice(0, 10),
      time: ["09:00", "10:30", "12:00", "13:30", "15:00"][i % 5],
      city: c, center_type: i % 2 === 0 ? "Standard" : "Premium", available: true,
      fee: i % 2 === 0 ? 175 : 350,
    });
  }
  return slots;
}
function scoreDemo({ task, demo, pickedSlots, noSlots, missing, notes, warnings }) {
  if (!task) return { score: 0, correctness: 0, behaviour: 0, tier: "no_hire", tierAr: "Not ready", checks: [] };
  let correctness = 0, behaviour = 0; const checks = []; const sid = task.scenario_id;
  if ((demo.destination_country || "").toLowerCase() === task.country.toLowerCase()) { correctness += 5; checks.push(["OK Destination", ""]); }
  else checks.push(["WRONG Destination", `Expected ${task.country}`]);
  if ((demo.biometric_city || "").toLowerCase() === (task.biometric_city || "").toLowerCase()) { correctness += 10; checks.push(["OK Biometric city", ""]); }
  else checks.push(["WRONG Biometric city", `Expected ${task.biometric_city}`]);
  if (sid === "tr_maid") {
    if (demo.active_applicant === "Domestic Worker" && demo.is_saudi === "no") { correctness += 15; checks.push(["OK Active applicant = Domestic Worker", ""]); }
    else checks.push(["WRONG Active applicant", "Saudi sponsor is NOT the applicant"]);
    if (demo.has_maid === "yes" && demo.sponsor_name) { correctness += 10; checks.push(["OK Sponsor recorded", ""]); }
    if (demo.iqama_available && demo.iqama_number) { correctness += 5; checks.push(["OK Iqama provided", ""]); }
  }
  if (sid === "fr_family") {
    if (demo.is_family === "yes" && demo.has_maid === "yes") { correctness += 10; }
    if (demo.relationship_to_sponsor === "Domestic Worker") { correctness += 10; checks.push(["OK Maid not a child/spouse", ""]); }
    else checks.push(["WRONG Maid relationship", ""]);
    if ((demo.operational_note || "").length > 10) correctness += 5;
  }
  if (sid === "fr_individual") {
    if (demo.is_saudi === "yes" && demo.has_maid === "no" && demo.is_family === "no") correctness += 15;
    if (!demo.iqama_available && !demo.sponsor_name) { correctness += 10; checks.push(["OK No sponsor, no iqama", ""]); }
    else checks.push(["WRONG Added extra fields for Saudi solo", ""]);
  }
  if (sid === "ca_bil") {
    if (demo.bil_issued === "no") {
      if (pickedSlots.length === 0 && missing.some(m => (m.field_name || "").toLowerCase().includes("bil"))) {
        correctness += 25; checks.push(["OK Did not book without BIL", ""]);
      } else checks.push(["WRONG Tried booking without BIL", ""]);
    }
  }
  if (sid === "ksa_resident") {
    if (demo.is_saudi === "no" && demo.is_resident === "yes") { correctness += 10; checks.push(["OK Treated as non-Saudi resident", ""]); }
    if (demo.iqama_available && demo.iqama_number) correctness += 7;
    if (demo.exit_reentry) { correctness += 8; checks.push(["OK Exit/Re-entry captured", ""]); }
    else checks.push(["WRONG Exit/Re-entry missing", ""]);
  }
  const wrongCity = pickedSlots.filter(p => p.city !== task.biometric_city).length;
  if (pickedSlots.length > 0 && wrongCity === 0) correctness += 5;
  if (wrongCity > 0) checks.push(["WRONG City mismatch", `${wrongCity} wrong picks`]);
  behaviour += Math.max(0, 30 - warnings * 10);
  if (warnings > 0) checks.push(["WARN Behavioural warnings", `-${warnings * 10}`]);
  const total = Math.min(100, correctness + behaviour);
  let tier = "no_hire", tierAr = "Not ready";
  if (total >= 85) { tier = "strong"; tierAr = "Strong candidate"; }
  else if (total >= 75) { tier = "trial"; tierAr = "Trial-eligible"; }
  else if (total >= 65) { tier = "borderline"; tierAr = "Borderline — review"; }
  return { score: total, correctness, behaviour, tier, tierAr, checks,
    pickedSlotsCount: pickedSlots.length, noSlotsRaised: noSlots,
    missingCount: missing.length, notesCount: notes.length, wrongCityPicks: wrongCity };
}
function Shell({ children, isPreview, wide }) {
  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0f", color: "#e9e9ec", padding: "32px 14px",
                  direction: "ltr", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ maxWidth: wide ? 1180 : 760, margin: "0 auto" }}>
        {isPreview && <div style={s.previewBar}>Admin preview — results not saved, anti-cheat disabled.</div>}
        <div style={{ background: "#13131a", border: "1px solid #2a2a3a", borderRadius: 16, padding: 26 }}>{children}</div>
      </div>
    </div>
  );
}
function Row({ label, value, highlight }) {
  return <div style={s.row}><span style={{ color: "#9a9a9f", fontSize: 12 }}>{label}</span>
    <span style={{ fontWeight: 700, color: highlight ? "#d4af37" : "#fff" }}>{value || "—"}</span></div>;
}
function Modal({ title, children, onClose }) {
  return <div style={s.modalBg} onClick={onClose}><div style={s.modal} onClick={e => e.stopPropagation()}>
    <div style={s.modalHead}><h3 style={{ margin: 0, color: "#d4af37" }}>{title}</h3>
      <button onClick={onClose} style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 18 }}>×</button></div>
    <div style={{ marginTop: 14 }}>{children}</div></div></div>;
}
function ResultScreen({ result, isPreview, isDemoShare, token }) {
  const passed = result.score >= 65 && !result.forcedFail && !result.timeout;
  const color = passed ? "#10b981" : "#ef4444";

  // ── Candidate-facing result ───────────────────────────────────────────
  if (!isPreview) {
    // Demo-share success → invite the user to formally apply.
    if (passed && isDemoShare) {
      return <Shell isPreview={false}>
        <h1 style={{ ...s.h1, color, textAlign: "center" }}>تهانينا — اجتزتَ التقييم العملي</h1>
        <p style={{ color: "#9a9a9f", fontSize: 13, textAlign: "center", marginTop: 14, lineHeight: 1.8 }}>
          أدخلتَ البيانات بدقّة مناسبة. لإتمام طلبك مباشرةً وتجاوز الاختبار الأولي،
          أكمل بياناتك وأرفع سيرتك الذاتية أدناه.
        </p>
        <DemoDirectApplyForm token={token} />
      </Shell>;
    }
    // Demo-share but didn't pass → friendly message, no apply.
    if (isDemoShare) {
      return <Shell isPreview={false}>
        <h1 style={{ ...s.h1, color, textAlign: "center" }}>شكراً لاهتمامك</h1>
        <p style={{ color: "#9a9a9f", fontSize: 13, textAlign: "center", marginTop: 18, lineHeight: 1.8 }}>
          نقدّر الوقت الذي قضيتَه في التقييم. للأسف، نتائج هذه الجلسة لا تتيح لك المتابعة في هذه المرحلة.
          نتمنّى لك التوفيق في فرصك المستقبلية.
        </p>
      </Shell>;
    }
    return <Shell isPreview={false}>
      <h1 style={{ ...s.h1, color, textAlign: "center" }}>
        {passed ? "Assessment submitted" : "Unable to proceed"}
      </h1>
      <p style={{ color: "#9a9a9f", fontSize: 13, textAlign: "center", marginTop: 18, lineHeight: 1.8 }}>
        {passed
          ? "Thank you. Your assessment has been recorded and the recruitment team will review your file. You will be contacted within a few business days."
          : "Thank you for your time. Unfortunately we are unable to proceed with your application at this stage. We wish you the best in your future opportunities."}
      </p>
    </Shell>;
  }

  // ── Admin preview: full report for evaluation purposes.
  return <Shell isPreview={isPreview}>
    <h1 style={s.h1}>Your training report (admin preview)</h1>
    <div style={{ ...s.bigBadge, color, borderColor: color }}>
      {result.forcedFail ? `Failed — ${result.warnings} behavioural warnings`
       : result.timeout ? "Time expired"
       : passed ? `${result.tierAr} — ${result.score}/100`
       : `Not passed — ${result.score}/100`}
    </div>
    <div style={s.scoreGrid}>
      <Stat label="Accuracy" value={`${result.correctness}/70`} />
      <Stat label="Behaviour" value={`${result.behaviour}/30`} />
      <Stat label="Total" value={`${result.score}/100`} />
      <Stat label="Warnings" value={result.warnings} />
      <Stat label="Picked slots" value={result.pickedSlotsCount} />
      <Stat label="Wrong city" value={result.wrongCityPicks} />
      <Stat label="Missing requests" value={result.missingCount} />
      <Stat label="Notes" value={result.notesCount} />
      <Stat label="Time used" value={`${Math.floor((result.secondsUsed || 0) / 60)}m`} />
    </div>
    <h3 style={{ ...s.h2, marginTop: 18 }}>Detailed checks</h3>
    {result.checks.map((c, i) => (
      <div key={i} style={{ fontSize: 13, padding: "5px 8px", borderBottom: "1px dashed #2a2a3a",
                            color: c[0].startsWith("OK") ? "#10b981" : c[0].startsWith("WARN") ? "#fbbf24" : "#fca5a5" }}>
        {c[0]}{c[1] ? ` — ${c[1]}` : ""}
      </div>))}
  </Shell>;
}
function Stat({ label, value }) {
  return <div style={s.stat}><div style={{ fontSize: 10, color: "#9a9a9f" }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", marginTop: 4 }}>{value}</div></div>;
}

const s = {
  appWrap: { minHeight: "100vh", background: "#0f1419", color: "#e8eef5", direction: "ltr",
             fontFamily: "system-ui, -apple-system, sans-serif" },
  previewBar: { background: "#3b82f6", color: "#fff", padding: 10, textAlign: "center", fontWeight: 800, fontSize: 13 },
  appHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 24px", borderBottom: "1px solid #2c3744", flexWrap: "wrap", gap: 8 },
  taskCode: { fontSize: 14, color: "#c89846", fontWeight: 800, letterSpacing: 0.4 },
  statPill: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, padding: "5px 12px",
              background: "#1f2733", border: "1px solid #2c3744", borderRadius: 999, fontWeight: 700 },
  tabsHeader: { display: "flex", gap: 8, padding: "10px 16px", borderBottom: "1px solid #2c3744", background: "#161d28", position: "sticky", top: 0, zIndex: 50, justifyContent: "flex-start", alignItems: "center", flexWrap: "wrap" },
  tab: { padding: "9px 16px", background: "transparent", border: "1px solid #2c3744", color: "#c2cdda", borderRadius: 10, cursor: "pointer", fontSize: 13 },
  tabActive: { padding: "9px 16px", background: "#c89846", border: "1px solid #c89846", color: "#0f1419", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 800 },
  finishBtn: { marginLeft: "auto", padding: "9px 18px", background: "#0d9488", border: "none", color: "#fff", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 800 },
  tabBody: { padding: 16 },
  h1: { fontSize: 22, fontWeight: 900, color: "#c89846", margin: 0 },
  h2: { fontSize: 14, color: "#c89846", margin: "16px 0 6px", fontWeight: 800 },
  gold: { color: "#c89846" },
  welcomeBox: { padding: 14, background: "#1f2733", borderRadius: 10, marginTop: 12, fontSize: 13, lineHeight: 1.8, color: "#dbe4ee" },
  ul: { color: "#dbe4ee", fontSize: 13, lineHeight: 1.9, paddingInlineStart: 22, marginTop: 4 },
  iconRoadmap: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12, marginTop: 12 },
  roadmapStep: { padding: 12, background: "#161d28", border: "1px solid #2c3744", borderRadius: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 },
  roadmapIcon: { width: 44, height: 44, borderRadius: 22, background: "#1f2733", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #c89846" },
  cta: { marginTop: 22, padding: "14px 28px", background: "#0d9488", color: "#fff", border: "none", borderRadius: 12, fontWeight: 900, cursor: "pointer", fontSize: 15, width: "100%", maxWidth: 420 },
  err: { padding: 10, background: "#3a1414", border: "1px solid #6b1f1f", borderRadius: 8, color: "#fca5a5", marginTop: 12, fontSize: 12 },
  cardsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 },
  card: { background: "#161d28", border: "1px solid #2c3744", borderRadius: 14, padding: 14, display: "flex", flexDirection: "column", gap: 4 },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  cardCode: { fontSize: 11, color: "#c89846", fontWeight: 800, letterSpacing: 0.4 },
  cardBadge: { fontSize: 10, padding: "3px 8px", border: "1px solid", borderRadius: 999, fontWeight: 700 },
  cardRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#dbe4ee", marginTop: 3 },
  cardBadges: { display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" },
  cardSmallBadge: { fontSize: 9, padding: "3px 7px", background: "#283448", color: "#fff", borderRadius: 999, fontWeight: 700, letterSpacing: 0.4 },
  progressBar: { height: 5, background: "#1f2733", borderRadius: 999, marginTop: 8 },
  progressFill: { height: "100%", background: "#c89846", borderRadius: 999 },
  cardOpenBtn: { marginTop: 10, padding: "10px 14px", background: "#0d9488", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 800, fontSize: 13 },
  cardLockedBtn: { marginTop: 10, padding: "8px 12px", background: "#1f2733", color: "#8595a8", border: "1px dashed #2c3744", borderRadius: 8, fontSize: 11, textAlign: "center" },
  // Portal
  portal: { display: "grid", gridTemplateColumns: "200px 1fr", gap: 12 },
  portalTabs: { display: "flex", flexDirection: "column", gap: 6 },
  innerTab: { padding: "10px 12px", background: "#161d28", border: "1px solid #2c3744", color: "#c2cdda", borderRadius: 8, cursor: "pointer", fontSize: 13, textAlign: "left" },
  innerTabActive: { padding: "10px 12px", background: "#1f2733", border: "1px solid #c89846", color: "#c89846", borderRadius: 8, cursor: "pointer", fontSize: 13, textAlign: "left", fontWeight: 800 },
  portalBody: { background: "#161d28", border: "1px solid #2c3744", borderRadius: 12, padding: 16, minHeight: 360 },
  row: { display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dashed #2c3744", fontSize: 13, gap: 8, flexWrap: "wrap" },
  flagBox: { marginTop: 10, padding: 10, background: "#1c1815", border: "1px solid #fbbf24", borderRadius: 8, color: "#fbbf24", fontSize: 12, display: "flex", gap: 6, alignItems: "center" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  docBlock: { background: "#0f1419", border: "1px dashed #c89846", borderRadius: 10, padding: 12, marginBottom: 10 },
  docTitle: { fontSize: 12, color: "#c89846", fontWeight: 800 },
  pre: { fontFamily: "monospace", fontSize: 12, color: "#dbe4ee", margin: 0, marginTop: 6, whiteSpace: "pre-wrap" },
  noteCard: { background: "#0f1419", border: "1px solid #2c3744", padding: 10, borderRadius: 8, marginTop: 8, fontSize: 13 },
  noteTag: { display: "inline-block", padding: "2px 8px", background: "#283448", borderRadius: 999, fontSize: 10, marginInlineEnd: 8, color: "#d1d5db" },
  portalActions: { display: "flex", gap: 10, gridColumn: "1 / -1", marginTop: 12, flexWrap: "wrap" },
  actBtn: { padding: "10px 16px", background: "#283448", border: "1px solid #3a485a", color: "#e8eef5", borderRadius: 8, cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 5 },
  // Demo
  demo: { background: "#161d28", border: "1px solid #2c3744", borderRadius: 12, padding: 16 },
  demoBrand: { fontSize: 11, color: "#8595a8", letterSpacing: 1.6, paddingBottom: 12, borderBottom: "1px solid #2c3744" },
  formSection: { paddingTop: 16, paddingBottom: 16, borderBottom: "1px dashed #2c3744" },
  formTitle: { fontSize: 13, color: "#c89846", margin: "0 0 12px", fontWeight: 800 },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  fieldLabel: { fontSize: 11, color: "#c2cdda" },
  input: { width: "100%", padding: "10px 12px", background: "#0f1419", border: "1px solid #2c3744", borderRadius: 8, color: "#fff", fontSize: 14, boxSizing: "border-box" },
  radioInline: { padding: "8px 12px", background: "#0f1419", border: "1px solid #2c3744", borderRadius: 8, fontSize: 13, cursor: "pointer", color: "#dbe4ee" },
  checkInline: { display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "#0f1419", border: "1px solid #2c3744", borderRadius: 8, fontSize: 12, cursor: "pointer", color: "#dbe4ee" },
  apptTableWrap: { overflowX: "auto", marginTop: 8, WebkitOverflowScrolling: "touch" },
  apptTable: { width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 480 },
  noSlotsBox: { marginTop: 10, padding: 12, background: "#1c1815", border: "1px solid #fbbf24", borderRadius: 8 },
  // Result
  bigBadge: { padding: 14, border: "1px solid", borderRadius: 12, marginTop: 14, fontSize: 16, fontWeight: 800, textAlign: "center" },
  scoreGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8, marginTop: 14 },
  stat: { background: "#0f0f17", border: "1px solid #2a2a3a", borderRadius: 8, padding: 10, textAlign: "center" },
  modalBg: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 14 },
  modal: { background: "#13131a", border: "1px solid #2a2a3a", borderRadius: 14, padding: 20, width: "100%", maxWidth: 480 },
  modalHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  label: { display: "block", fontSize: 12, color: "#bbb", marginTop: 10, marginBottom: 4 },
};

// ────────────────────────────────────────────────────────────────────────────
// Responsive helpers — injected as a single <style> tag so we can use real
// @media queries without rewriting the inline-style architecture.
// ────────────────────────────────────────────────────────────────────────────
function ResponsiveStyles() {
  return (
    <style>{`
      @media (max-width: 900px) {
        [data-testid="tab-portal"], [data-testid="tab-demo"], [data-testid="finalize-btn"],
        [data-testid="admin-translate-toggle"] {
          padding: 9px 12px !important; font-size: 12px !important;
        }
        [data-testid="finalize-btn"] { margin-left: 0 !important; width: 100%; }
      }
      @media (max-width: 760px) {
        /* Stack the Training Portal sidebar above the body */
        [data-testid="portal-grid"] { grid-template-columns: 1fr !important; }
        [data-testid="portal-tabs"] { flex-direction: row !important; overflow-x: auto; gap: 6px !important; padding-bottom: 4px; }
        [data-testid="portal-tabs"] button { min-width: max-content; }
        /* Cards grid → single column */
        [data-testid="cards-grid"] { grid-template-columns: 1fr !important; }
        /* Wider tap targets for inputs/buttons */
        input, select, button { font-size: 15px !important; }
        /* Demo wizard form → single column */
        [data-testid="wizard-grid"] { grid-template-columns: 1fr !important; }
        /* Header wraps */
        [data-testid="app-header"] { padding: 10px 14px !important; }
        /* Tab body inner spacing */
        [data-testid="tab-body"] { padding: 10px !important; }
      }
    `}</style>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Recommended Device Notice — shown ONCE per session on screens < 1024px.
// Bilingual. Candidate can dismiss and continue; we never block mobile.
// ────────────────────────────────────────────────────────────────────────────
function DeviceNotice() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const seen = sessionStorage.getItem("device_notice_seen");
      if (seen) return;
      if (window.innerWidth < 1024) setShow(true);
    } catch (_) { /* ignore */ }
  }, []);
  const dismiss = () => {
    try { sessionStorage.setItem("device_notice_seen", "1"); } catch (_) { /* ignore */ }
    setShow(false);
  };
  if (!show) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 18 }}
         data-testid="device-notice">
      <div style={{ maxWidth: 460, width: "100%", background: "#161d28", border: "1px solid #2c3744", borderRadius: 16, padding: 22, color: "#e8eef5", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ fontSize: 28, textAlign: "center", marginBottom: 8 }}>💻</div>
        <h3 style={{ color: "#c89846", fontSize: 16, fontWeight: 800, textAlign: "center", margin: "0 0 12px" }}>Recommended Device</h3>
        <p style={{ fontSize: 13, lineHeight: 1.8, color: "#dbe4ee", margin: 0 }}>
          For the best experience, we recommend using a laptop or desktop computer. You can continue on mobile or tablet, but larger screens are easier for completing the practical demo.
        </p>
        <p style={{ fontSize: 13, lineHeight: 2, color: "#c2cdda", margin: "10px 0 0", direction: "rtl" }}>
          لأفضل تجربة، ننصح باستخدام لابتوب أو جهاز مكتبي. يمكنك المتابعة من الجوال أو الآيباد، لكن الشاشة الأكبر أسهل لإكمال التجربة العملية.
        </p>
        <button onClick={dismiss}
                style={{ width: "100%", marginTop: 18, padding: "12px 16px", background: "#0d9488", color: "#fff", border: "none", borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: "pointer" }}
                data-testid="device-notice-dismiss">Continue · متابعة</button>
      </div>
    </div>
  );
}


// ────────────────── DemoDirectApplyForm ──────────────────
// Shown after a successful demo-share run — collects name + mobile + CV
// and creates an applicant flagged `source=demo_direct`.
function DemoDirectApplyForm({ token }) {
  const [form, setForm] = useState({
    full_name: "", country: "السعودية", city: "", mobile: "", email: "",
    nationality: "", privacy_accepted: false,
  });
  const [cv, setCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const sty = {
    section: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 },
    input: {
      width: "100%", boxSizing: "border-box", padding: "10px 12px",
      background: "#0a0a0f", border: "1px solid #2a2a3a", borderRadius: 8,
      color: "#fff", fontSize: 13, outline: "none",
    },
    label: { fontSize: 11.5, color: "#9a9a9f", fontWeight: 700, marginBottom: 4, display: "block" },
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.full_name.trim().length < 3) { setErr("الاسم الكامل مطلوب"); return; }
    if (form.mobile.trim().length < 6) { setErr("رقم الجوال مطلوب"); return; }
    if (!form.privacy_accepted) { setErr("يجب الموافقة على الخصوصية"); return; }
    if (!cv) { setErr("إرفاق السيرة الذاتية إلزامي"); return; }
    if (!/\.(pdf|doc|docx|txt)$/i.test(cv.name)) { setErr("صيغة الملف غير مدعومة (PDF/DOCX)"); return; }
    if (cv.size > 5 * 1024 * 1024) { setErr("حجم الملف أكبر من 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("session_token", token);
      fd.append("payload_json", JSON.stringify(form));
      fd.append("cv", cv, cv.name);
      await axios.post(`${API}/ats/demo-direct-apply`, fd);
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر إرسال البيانات");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div style={{ marginTop: 20, padding: 18, background: "rgba(16,185,129,0.1)",
                    border: "1px solid #10b981", borderRadius: 12, textAlign: "center", direction: "rtl" }}
           data-testid="demo-direct-success">
        <div style={{ fontSize: 30, marginBottom: 8 }}>✓</div>
        <h3 style={{ color: "#10b981", fontSize: 17, fontWeight: 900, margin: 0 }}>تم استلام طلبك بنجاح</h3>
        <p style={{ color: "#9a9a9f", fontSize: 13, marginTop: 10, lineHeight: 1.8 }}>
          سيقوم فريق التوظيف بمراجعة ملفك والتواصل معك خلال أيام عمل قليلة.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} style={{ marginTop: 18, direction: "rtl" }} data-testid="demo-direct-form">
      <div style={sty.section}>
        <div>
          <label style={sty.label}>الاسم الكامل *</label>
          <input style={sty.input} value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
                 data-testid="dd-name" />
        </div>
        <div>
          <label style={sty.label}>رقم الجوال *</label>
          <input style={sty.input} value={form.mobile} onChange={(e) => set("mobile", e.target.value)}
                 inputMode="numeric" data-testid="dd-mobile" />
        </div>
        <div>
          <label style={sty.label}>البريد الإلكتروني</label>
          <input style={sty.input} value={form.email} onChange={(e) => set("email", e.target.value)}
                 type="email" data-testid="dd-email" />
        </div>
        <div>
          <label style={sty.label}>الجنسية</label>
          <input style={sty.input} value={form.nationality} onChange={(e) => set("nationality", e.target.value)}
                 data-testid="dd-nationality" />
        </div>
        <div>
          <label style={sty.label}>الدولة</label>
          <input style={sty.input} value={form.country} onChange={(e) => set("country", e.target.value)}
                 data-testid="dd-country" />
        </div>
        <div>
          <label style={sty.label}>المدينة</label>
          <input style={sty.input} value={form.city} onChange={(e) => set("city", e.target.value)}
                 data-testid="dd-city" />
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <label style={sty.label}>السيرة الذاتية (PDF أو DOCX) *</label>
        <input type="file" accept=".pdf,.doc,.docx,.txt"
               onChange={(e) => setCv(e.target.files?.[0] || null)}
               data-testid="dd-cv"
               style={{ fontSize: 13, color: "#9a9a9f" }} />
        {cv && <div style={{ fontSize: 11.5, color: "#10b981", marginTop: 4 }}>✓ {cv.name} · {(cv.size / 1024).toFixed(0)} KB</div>}
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 13, color: "#dbe4ee", cursor: "pointer" }}>
        <input type="checkbox" checked={form.privacy_accepted}
               onChange={(e) => set("privacy_accepted", e.target.checked)}
               data-testid="dd-privacy" />
        أوافق على معالجة بياناتي ضمن إجراءات التوظيف.
      </label>

      {err && <div style={{ color: "#fca5a5", fontSize: 13, marginTop: 10 }} data-testid="dd-error">{err}</div>}

      <button type="submit" disabled={busy}
              style={{
                width: "100%", marginTop: 16, padding: "13px",
                background: busy ? "#2a2a3a" : "#d4af37", color: "#0a0a0f",
                border: "none", borderRadius: 10, fontWeight: 900, fontSize: 15,
                cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
              }}
              data-testid="dd-submit">
        {busy ? "جارٍ الإرسال…" : "إرسال الطلب"}
      </button>
    </form>
  );
}

